import { GuestSession, isGuestSession } from './guestSession'

/**
 * Ask the server who a play key belongs to — or for a brand new guest when no
 * key is given.
 *
 * This runs on the UNAUTHENTICATED socket: it is the one exchange a visitor can
 * make before they have any identity at all. The reply is what the browser then
 * reconnects with, which is the moment they become a real player.
 */

export const GUEST_IDENTIFY_TIMEOUT_MS = 15000

export interface IdentifySocket {
  on: (event: string, listener: (payload?: any) => void) => void
  off: (event: string, listener: (payload?: any) => void) => void
  emit: (event: string, ...args: any[]) => void
}

export const GUEST_IDENTIFY_UNAVAILABLE = 'Not connected to the game server yet. Please try again in a moment.'
export const GUEST_IDENTIFY_TIMED_OUT = 'The game server did not answer. Please try again.'
export const GUEST_IDENTIFY_FAILED = 'Could not start a guest session. Please try again.'

export const requestGuestIdentity = (
  socket: IdentifySocket | undefined | null,
  token?: string,
  timeoutMs: number = GUEST_IDENTIFY_TIMEOUT_MS,
): Promise<GuestSession> => new Promise((resolve, reject) => {
  if (!socket) {
    reject(new Error(GUEST_IDENTIFY_UNAVAILABLE))

    return
  }

  let settled = false
  let timer: ReturnType<typeof setTimeout>

  const settle = (run: () => void) => {
    if (settled) {
      return
    }
    settled = true
    clearTimeout(timer)
    socket.off('guest_identity', onIdentity)
    socket.off('guest_identity_error', onFailure)
    run()
  }

  const onIdentity = (identity?: any) => settle(() => {
    // Anything that isn't a usable session would strand the player as a
    // half-signed-in ghost, so treat it as a failure rather than storing it.
    if (isGuestSession(identity)) {
      resolve({ uid: identity.uid, token: identity.token, name: identity.name })
    } else {
      reject(new Error(GUEST_IDENTIFY_FAILED))
    }
  })

  const onFailure = (error?: any) => settle(() => reject(new Error(error?.message || GUEST_IDENTIFY_FAILED)))

  timer = setTimeout(() => settle(() => reject(new Error(GUEST_IDENTIFY_TIMED_OUT))), timeoutMs)

  socket.on('guest_identity', onIdentity)
  socket.on('guest_identity_error', onFailure)
  socket.emit('guest_identify', token)
})
